import { Clock, Mail, AlertCircle, CheckCircle2 } from "lucide-react";

const FIELD_LABEL = {
  email: "Email",
  phone: "Cellulare 1",
  tax_code: "Codice Fiscale",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return String(iso).slice(0, 16);
  }
}

/**
 * Full-page "procedure halted" state shown when the customer has an
 * open pre-signature correction request. The wallet form is hidden
 * until the dealer resolves the request.
 *
 * Fabrizio CR 31/07/2026 (option 2.a — blocco totale + option 3.b —
 * new link inviato al nuovo indirizzo).
 */
export default function CorrectionPendingState({
  request,
  customerEmail,
  translations = {},
}) {
  const t = {
    title:
      translations.title || "Procedura sospesa in attesa del concessionario",
    subtitle:
      translations.subtitle ||
      "Abbiamo inoltrato la tua richiesta di correzione al concessionario che ha creato il tuo contratto. La procedura di attivazione riprenderà automaticamente non appena il concessionario avrà aggiornato i dati.",
    field_label: translations.field_label || "Campo richiesto in modifica",
    current_label: translations.current_label || "Valore attuale (errato)",
    requested_label: translations.requested_label || "Valore corretto richiesto",
    note_label: translations.note_label || "La tua nota",
    submitted_at: translations.submitted_at || "Richiesta inviata il",
    what_happens: translations.what_happens || "Cosa succede ora?",
    step_1:
      translations.step_1 ||
      "Il concessionario riceve una email con la tua segnalazione e la vede nel suo portale.",
    step_2:
      translations.step_2 ||
      "Il concessionario correggerà i dati nel tuo profilo (o rifiuterà la richiesta se ritiene i dati corretti).",
    step_3:
      translations.step_3 ||
      "Se approvata, riceverai una NUOVA email di attivazione con un nuovo link a questa pagina — assicurati di controllare anche la cartella spam.",
    step_4:
      translations.step_4 ||
      "Riprenderai la procedura dallo Step 1 con i dati corretti già precompilati.",
    email_notice: translations.email_notice || "Ti scriveremo a:",
    contact_dealer:
      translations.contact_dealer ||
      "Per urgenze contatta direttamente il tuo concessionario.",
    // Reassuring "you can close this page" panel (Zorzutti CR 03/08/2026).
    // Removes the anxiety of "do I have to keep this tab open?" and
    // reinforces the promise of the follow-up email so the customer
    // walks away calmly instead of refreshing the page every hour.
    close_title: translations.close_title || "Puoi chiudere questa pagina",
    close_body:
      translations.close_body ||
      "Ti scriveremo entro 24 ore, appena il concessionario avrà aggiornato i tuoi dati. Non è necessario tenere questa pagina aperta.",
  };

  const fieldLabel = FIELD_LABEL[request?.field] || request?.field || "—";

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4"
      data-testid="correction-pending-state"
    >
      <div className="w-full max-w-2xl">
        <div className="rounded-2xl border border-amber-800/40 bg-gradient-to-br from-amber-950/30 via-[#141414] to-[#141414] shadow-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-800 bg-amber-950/20">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-amber-900/40 border border-amber-800">
                <Clock size={22} className="text-amber-300" />
              </div>
              <div>
                <h1 className="text-white text-xl font-semibold">{t.title}</h1>
                <p className="text-amber-200/70 text-xs mt-0.5">
                  {t.submitted_at} {fmtDate(request?.requested_at)}
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <p className="text-gray-300 leading-relaxed">{t.subtitle}</p>

            <div className="rounded-xl border border-gray-800 bg-[#0A0A0A] p-4 space-y-3">
              <div>
                <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">
                  {t.field_label}
                </div>
                <div className="text-white font-medium">{fieldLabel}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-gray-500 text-xs mb-1">
                    {t.current_label}
                  </div>
                  <div className="text-gray-400 font-mono text-sm break-all">
                    {request?.current_value || "(vuoto)"}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs mb-1">
                    {t.requested_label}
                  </div>
                  <div className="text-red-400 font-mono text-sm break-all font-semibold">
                    {request?.new_value}
                  </div>
                </div>
              </div>
              {request?.note && (
                <div>
                  <div className="text-gray-500 text-xs mb-1">
                    {t.note_label}
                  </div>
                  <div className="text-gray-300 text-sm italic">
                    "{request.note}"
                  </div>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <AlertCircle size={16} className="text-blue-400" />
                {t.what_happens}
              </h3>
              <ol className="space-y-2 text-sm text-gray-300 pl-5 list-decimal">
                <li>{t.step_1}</li>
                <li>{t.step_2}</li>
                <li>{t.step_3}</li>
                <li>{t.step_4}</li>
              </ol>
            </div>

            {customerEmail && (
              <div className="rounded-lg border border-blue-800/40 bg-blue-950/20 p-3 flex items-center gap-3">
                <Mail size={16} className="text-blue-400 flex-shrink-0" />
                <div className="text-sm">
                  <span className="text-gray-400">{t.email_notice}</span>{" "}
                  <span className="text-blue-300 font-mono">
                    {customerEmail}
                  </span>
                </div>
              </div>
            )}

            {/* "You can close this page" reassurance panel — kept low-key
                (emerald tint, no CTA, no timer) so it feels like a
                friendly nudge, not a dismissal. */}
            <div
              className="rounded-lg border border-emerald-800/40 bg-emerald-950/20 p-4 flex items-start gap-3"
              data-testid="correction-pending-close-panel"
            >
              <CheckCircle2
                size={20}
                className="text-emerald-400 flex-shrink-0 mt-0.5"
              />
              <div className="text-sm">
                <div className="text-emerald-200 font-medium mb-1">
                  {t.close_title}
                </div>
                <div className="text-gray-300 leading-relaxed">
                  {t.close_body}
                </div>
              </div>
            </div>

            <p className="text-gray-500 text-xs italic border-t border-gray-800 pt-4">
              {t.contact_dealer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
